/**
 * WorkspaceProvider — Feature 075 (plan 013, Workstream B).
 *
 * App-level provider that, for SUB_DELEGATE users only, loads the
 * workspaces they can act in, keeps the persisted selection valid, and
 * exposes `switchWorkspace`. For everyone else it is a strict no-op
 * (no query fired, store never set, `effectiveRole = user.role`).
 *
 * Switch semantics: set the store (axios header + socket handshake pick it
 * up automatically) → clear the React Query cache (everything visible is
 * owner-scoped) → consumers navigate to /dashboard for re-routing.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { activeWorkspaceStore } from '@/shared/workspace/activeWorkspaceStore';
import type { UserRole } from '@/core/auth/types';
import type { DelegationPermission } from '@/shared/components/sub-delegation';
import { listMyWorkspaces, type MyWorkspace } from '../api/myWorkspacesApi';

export type WorkspaceContextValue = {
  /** True when the logged-in user is a SUB_DELEGATE. */
  isDelegate: boolean;
  /** True while a delegate's workspace list is loading. */
  isLoading: boolean;
  workspaces: MyWorkspace[];
  activeWorkspace: MyWorkspace | null;
  switchWorkspace: (ownerId: string) => void;
  /** Workspace owner's role for delegates; the user's own role otherwise. */
  effectiveRole: UserRole | null;
  /**
   * Permissions granted on the active workspace. Empty for non-delegates
   * (who implicitly hold every permission — see `hasWorkspacePermission`).
   */
  permissions: DelegationPermission[];
  /**
   * Whether the current actor may use a feature. Always `true` for
   * non-delegates (owners have full access to their own workspace); for
   * delegates it checks the active workspace's granted permissions.
   */
  hasWorkspacePermission: (permission: DelegationPermission) => boolean;
};

const WorkspaceContext = createContext<WorkspaceContextValue>({
  isDelegate: false,
  isLoading: false,
  workspaces: [],
  activeWorkspace: null,
  switchWorkspace: () => undefined,
  effectiveRole: null,
  permissions: [],
  hasWorkspacePermission: () => true,
});

export const myWorkspacesQueryKey = ['workspace', 'my-workspaces'] as const;

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const isDelegate = isAuthenticated && user?.role === 'SUB_DELEGATE';

  const workspacesQuery = useQuery({
    queryKey: myWorkspacesQueryKey,
    queryFn: listMyWorkspaces,
    enabled: isDelegate,
    staleTime: 60_000,
  });

  const activeOwnerId = useSyncExternalStore(
    activeWorkspaceStore.subscribe,
    activeWorkspaceStore.get,
  );

  const workspaces = useMemo(
    () => (isDelegate ? (workspacesQuery.data ?? []) : []),
    [isDelegate, workspacesQuery.data],
  );

  // Keep the persisted selection valid: clear it for non-delegates, replace
  // it when it no longer matches an ACTIVE workspace, auto-select the first
  // workspace on first load.
  useEffect(() => {
    if (!isDelegate) {
      if (activeWorkspaceStore.get() !== null) activeWorkspaceStore.clear();
      return;
    }
    if (!workspacesQuery.data) return;

    const stillValid = workspacesQuery.data.some(
      (workspace) => workspace.ownerId === activeWorkspaceStore.get(),
    );
    if (!stillValid) {
      activeWorkspaceStore.set(workspacesQuery.data[0]?.ownerId ?? null);
    }
  }, [isDelegate, workspacesQuery.data]);

  const activeWorkspace = useMemo(
    () =>
      workspaces.find((workspace) => workspace.ownerId === activeOwnerId) ??
      null,
    [workspaces, activeOwnerId],
  );

  const switchWorkspace = useCallback(
    (ownerId: string) => {
      if (ownerId === activeWorkspaceStore.get()) return;
      activeWorkspaceStore.set(ownerId);
      // Everything cached is scoped to the previous owner.
      queryClient.clear();
    },
    [queryClient],
  );

  const effectiveRole: UserRole | null = isDelegate
    ? (activeWorkspace?.ownerRole ?? null)
    : (user?.role ?? null);

  const permissions = useMemo<DelegationPermission[]>(
    () => (isDelegate ? (activeWorkspace?.permissions ?? []) : []),
    [isDelegate, activeWorkspace],
  );

  const hasWorkspacePermission = useCallback(
    (permission: DelegationPermission) =>
      isDelegate ? permissions.includes(permission) : true,
    [isDelegate, permissions],
  );

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      isDelegate,
      isLoading: isDelegate && workspacesQuery.isLoading,
      workspaces,
      activeWorkspace,
      switchWorkspace,
      effectiveRole,
      permissions,
      hasWorkspacePermission,
    }),
    [
      isDelegate,
      workspacesQuery.isLoading,
      workspaces,
      activeWorkspace,
      switchWorkspace,
      effectiveRole,
      permissions,
      hasWorkspacePermission,
    ],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace(): WorkspaceContextValue {
  return useContext(WorkspaceContext);
}
